import axiosInstance from "../axiosInstance";

export const inventoryService = {
  // Trigger a bulk inventory import from the client's OMS provider(s)
  // (e.g. Pipe17 per-location stock). Mirrors productService.syncProduct.
  syncInventory: async (data) => {
    try {
      const response = await axiosInstance.post(`/sync-inventory`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Per-warehouse inventory rows for a single variant. A SKU has one inventory
  // row PER WAREHOUSE (unique key: sku+client+provider+warehouse), so this can
  // return several rows; the product details modal calls it lazily, one variant
  // at a time, when its warehouse accordion is opened.
  fetchByProductSku: async (productId, sku, signal) => {
    try {
      const api =
        `/inventory?product=${encodeURIComponent(productId)}` +
        `&sku=${encodeURIComponent(sku)}` +
        `&$populate[0][path]=warehouse` +
        `&$populate[0][select][0]=name` +
        `&$populate[0][select][1]=warehouse_code` +
        `&$populate[1][path]=provider` +
        `&$populate[1][select][0]=name` +
        `&$limit=200&$sort[in_hand]=-1`;

      // The accordion shows its own inline error, so keep the global toast out
      // of it — a variant with no inventory rows is a normal state, not an
      // app-level failure worth interrupting the operator for.
      const response = await axiosInstance.get(api, {
        suppressErrorToast: true,
        ...(signal ? { signal } : {}),
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  fetch: async (query = "", signal) => {
    try {
      let api = `/inventory?`;

      if (!query.includes("$sort")) {
        api += `&$sort[updatedAt]=-1`;
      }

      // Append any extra query string provided
      if (query && query.length) api += `&${query}`;

      // Pass signal as axios config if provided
      const response = await axiosInstance.get(
        api,
        signal ? { signal } : undefined,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  fetchInventoryProduct: async (
    client_id,
    searchValue,
    query = "",
    signal,
    limit,
    skip,
  ) => {
    try {
      // Build base query for inventory-products
      const limitParam =
        typeof limit !== "undefined" && limit !== null ? limit : "";
      const skipParam =
        typeof skip !== "undefined" && skip !== null ? skip : "";

      let api_query = `/inventory-products?$limit=${limitParam}&$skip=${skipParam}&$sort[name]=1`;

      if (client_id) api_query += `&client=${client_id}`;
      if (searchValue)
        api_query += `&$search=${encodeURIComponent(searchValue)}`;
      if (query && query.length) api_query += `&${query}`;

      const response = await axiosInstance.get(
        api_query,
        signal ? { signal } : undefined,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  create: async (obj) => {
    try {
      return obj;
    } catch (error) {
      throw error;
    }
  },

  edit: async (id, obj) => {
    try {
      const response = await axiosInstance.patch(`inventory/${id}`, obj);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  delete: async (id) => {
    try {
      return { id };
    } catch (error) {
      throw error;
    }
  },

  forecast: async (client_id, sku) => {
    try {
      const response = await axiosInstance.post("forecast", {
        client: client_id,
        input: { sku, horizon: 6, type: "Monthly" },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  bulkForecast: async (obj) => {
    try {
      const response = await axiosInstance.post("forecast", obj);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  fetchForecastHistory: async (client_id, sku) => {
    try {
      const response = await axiosInstance.get(
        `/forecast-data?$sort[createdAt]=-1&client=${client_id}&forecast_type=warehouse&sku=${encodeURIComponent(
          sku,
        )}&$populate[0][path]=input.warehouse&$populate[0][select]=name&$populate[0][select]=warehouse_code`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  fetchInventoryOnhand: async (searchKey, client_id, limit = 15, skip = 0) => {
    try {
      let query = `limit=${limit}&skip=${skip}`;
      if (client_id) {
        query += `&client=${client_id}`;
      }
      if (searchKey) {
        query += `&searchkey=${encodeURIComponent(searchKey)}`;
      }
      let response = await axiosInstance.get(
        `/fetch-inventory-onhand?${query}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  fetchInventoryIntransit: async (
    searchKey,
    client_id,
    limit = 15,
    skip = 0,
  ) => {
    try {
      let query = `limit=${limit}&skip=${skip}`;
      if (client_id) {
        query += `&client=${client_id}`;
      }
      if (searchKey) {
        query += `&searchkey=${encodeURIComponent(searchKey)}`;
      }
      let response = await axiosInstance.get(
        `/fetch-inventory-in-transit?${query}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  fetchInventoryRecorderThreshold: async (
    searchKey,
    client_id,
    limit = 15,
    skip = 0,
  ) => {
    try {
      let query = `limit=${limit}&skip=${skip}`;
      if (client_id) {
        query += `&client=${client_id}`;
      }
      if (searchKey) {
        query += `&searchkey=${encodeURIComponent(searchKey)}`;
      }
      let response = await axiosInstance.get(
        `/inventory-reorder-point?${query}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  updateForecastMetrics: async (data) => {
    try {
      const response = await axiosInstance.patch(
        `/forecast/${data.client}`,
        data,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  discardForecastMetrics: async (id) => {
    try {
      const response = await axiosInstance.delete(`/forecast/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  addForecastResult: async (id, record) => {
    try {
      const response = await axiosInstance.put(`/forecast/${id}`, record);
      return response.data;
    } catch (e) {
      throw e;
    }
  },
  fetchSimilarProduct: async (obj) => {
    try {
      const response = await axiosInstance.post(`/similar-product`, obj);
      return response.data;
    } catch (e) {
      throw e;
    }
  },
};
